import * as THREE from 'three';

/** Built-in block texture faces served from `client/public/textures/core/`. */
export type CraftFace = 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';

/** Supported core-block face edge lengths (pixels). */
export const CORE_FACE_SIZE_OPTIONS = [32, 64, 128, 256] as const;
export type CoreFaceSize = (typeof CORE_FACE_SIZE_OPTIONS)[number];

export const CRAFT_FACES: readonly CraftFace[] = [
  'right', 'left', 'top', 'bottom', 'front', 'back',
] as const;

/** Single Minecraft box-UV sheet for the craft block. */
const CRAFT_ATLAS_URL = '/textures/core/craft/atlas.png';

/** BoxGeometry face index → craft face name. */
const FACE_BY_INDEX: CraftFace[] = [
  'right', 'left', 'top', 'bottom', 'front', 'back',
];

const craftImages = new Map<CraftFace, HTMLCanvasElement>();

export function isValidCoreFaceSize(value: number): value is CoreFaceSize {
  return (CORE_FACE_SIZE_OPTIONS as readonly number[]).includes(value);
}

function nearestCoreFaceSize(raw: number): CoreFaceSize {
  let best: CoreFaceSize = CORE_FACE_SIZE_OPTIONS[0];
  let bestDist = Infinity;
  for (const option of CORE_FACE_SIZE_OPTIONS) {
    const dist = Math.abs(option - raw);
    if (dist < bestDist) {
      best = option;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Detect atlas layout and face size from pixel dimensions.
 * box-UV = 4S×2S (preferred), legacy = 3S×2S, single = S×S (all faces).
 */
export function detectCoreTextureLayout(
  width: number,
  height: number,
): { layout: 'box' | 'legacy' | 'single'; faceSize: CoreFaceSize } {
  const aspect = width / Math.max(1, height);
  if (aspect > 1.75) {
    const fromH = height / 2;
    const fromW = width / 4;
    const raw = Math.abs(fromW - fromH) <= 1 ? (fromW + fromH) / 2 : fromH;
    return { layout: 'box', faceSize: nearestCoreFaceSize(raw) };
  }
  if (aspect > 1.25) {
    const fromH = height / 2;
    const fromW = width / 3;
    const raw = Math.abs(fromW - fromH) <= 1 ? (fromW + fromH) / 2 : fromH;
    return { layout: 'legacy', faceSize: nearestCoreFaceSize(raw) };
  }
  if (Math.abs(width - height) <= 1) {
    return { layout: 'single', faceSize: nearestCoreFaceSize(width) };
  }
  throw new Error(
    `Unrecognized craft atlas ${width}×${height}. ` +
      `Use Minecraft box-UV 4S×2S (S = ${CORE_FACE_SIZE_OPTIONS.join(', ')}).`,
  );
}

function boxUvOffset(face: number, size: number): { x: number; y: number } {
  switch (face) {
    case 0: return { x: 0, y: size };
    case 1: return { x: size * 2, y: size };
    case 2: return { x: size, y: 0 };
    case 3: return { x: size * 2, y: 0 };
    case 4: return { x: size, y: size };
    case 5: return { x: size * 3, y: size };
    default: return { x: 0, y: 0 };
  }
}

function legacyOffset(face: number, size: number): { x: number; y: number } {
  return { x: (face % 3) * size, y: Math.floor(face / 3) * size };
}

/** Load craft block from the single box-UV `atlas.png`. */
export async function preloadCoreTextures(): Promise<void> {
  craftImages.clear();
  const atlas = await loadImage(CRAFT_ATLAS_URL);
  splitAtlasIntoFaces(atlas);
}

function splitAtlasIntoFaces(atlas: HTMLImageElement): void {
  const { layout, faceSize } = detectCoreTextureLayout(
    atlas.naturalWidth,
    atlas.naturalHeight,
  );
  if (layout === 'single') {
    for (const face of CRAFT_FACES) {
      craftImages.set(face, drawFace(atlas, 0, 0, atlas.naturalWidth, faceSize));
    }
    return;
  }
  const offsetOf = layout === 'box' ? boxUvOffset : legacyOffset;
  const atlasW = layout === 'box' ? faceSize * 4 : faceSize * 3;
  const atlasH = faceSize * 2;
  const sheet = document.createElement('canvas');
  sheet.width = atlasW;
  sheet.height = atlasH;
  const ctx = sheet.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(atlas, 0, 0, atlasW, atlasH);
  FACE_BY_INDEX.forEach((name, index) => {
    const { x, y } = offsetOf(index, faceSize);
    craftImages.set(name, drawFace(sheet, x, y, faceSize, faceSize));
  });
}

function drawFace(
  source: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  size: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(source, sx, sy, sw, sw, 0, 0, size, size);
  return canvas;
}

export function getCraftFaceImage(face: CraftFace | 'side'): HTMLCanvasElement {
  const resolved: CraftFace = face === 'side' ? 'left' : face;
  const image = craftImages.get(resolved);
  if (!image) {
    throw new Error(`Core craft texture “${resolved}” not loaded — call preloadCoreTextures()`);
  }
  return image;
}

/** Inventory / 2D preview: paint a sliced face onto a canvas. */
export function createCraftTextureCanvas(face: CraftFace | 'side'): HTMLCanvasElement {
  const image = getCraftFaceImage(face);
  const canvas = document.createElement('canvas');
  canvas.width = image.width || 32;
  canvas.height = image.height || 32;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(image, 0, 0);
  return canvas;
}

export function createCraftFaceMaterial(face: CraftFace): THREE.MeshLambertMaterial {
  const texture = new THREE.Texture(getCraftFaceImage(face));
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return new THREE.MeshLambertMaterial({ map: texture });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load texture ${url}`));
    image.src = url;
  });
}
